import { createReducer } from "@reduxjs/toolkit";
import { typePagination } from "../system/constants";
import { logoutAC, resetReducers } from "../system/systemActionCreators";
import {
  dOrdersArchiveSetDataAC,
  dOrdersArchiveSetFiltersAC,
  dOrdersArchiveClearFiltersAC,
  dOrdersArchiveSetDetailItemAC,
  dOrdersArchiveSetPageNumberAC,
  dOrdersArchiveSetPaginationAC,
  dOrdersArchiveClearDetailItemAC,
  dOrdersArchiveSetItemsForPageAC,
} from "./actionCreators";

export type typeOrdersFilters = {
  externalId?: string | null;
  dateCreated?: {
    from: string | null;
    to: string | null;
  } | null;
  searchString?: string | null;
  number?: string | number | null;
};

export interface Pagination {
  pageSize: number;
  pageNumber: number;
  totalPages: number;
  totalElements: number;
  numberOfElements: number;
}

export interface IContent {
  id: string;
  store: Store;
  phone: string;
  saleId: string;
  number: number;
  status: string;
  address: string;
  storeId: string;
  comment: string;
  courierId: string;
  createdBy: string;
  updatedBy: string;
  externalId: string;
  discount: Discount;
  collectorId: string;
  dateCreated: string;
  dateUpdated: string;
  payments: Payment[];
  customerName: string;
  orderedProducts: IProduct[];
  acceptedProducts: IProduct[];
  rejectedProducts: IProduct[];
  servicePayment: SaleServicePayment;
}

export interface Store {
  id: string;
  name: string;
  email: string;
  address: string;
  manager: string;
  deleted: boolean;
  externalId: string;
  industryId: string;
  phoneNumber: string;
  description: string;
  interfaceType: string;
  externalSystemCode: string;
  managerPhoneNumber: string;
}

export interface Payment {
  id: string;
  date: string;
  method: string;
  amount: number;
  paymentType: string;
  paymentAmount: number;
  paymentInfo: PaymentInfo;
  transactionNumber: number;
  paymentTransactionValue: number;
}

export interface Discount {
  value: number;
  isInPercents: boolean;
}

export interface PaymentInfo {
  rrn: string;
  stan: string;
  authCode: string;
  terminalId: string;
  merchantId: string;
  responseCode: string;
  paymentSystemName: string;
  transactionNumber: number;
}

export interface IProduct {
  id: string;
  tax: number;
  psid: string;
  price: number;
  amount: number;
  marked: boolean;
  product?: {
    vat: number;
    name: string;
  };
  unitCode: number;
  labels?: string[];
  productId: string;
  barcodes: string[];
  customTotal: number;
  productName: string;
  packageCode: string;
  commissionTIN: string;
  markedLabels: string[];
  discountAmount: number;
  commissionPINFL: string;
  productOwnerType: string;
}

export interface ServicePayment {
  value: number;
  isInPercents: boolean;
}

// sale types

export interface IDetailSale {
  id: string;
  pos: SalePos;
  date: string;
  fmId: string;
  paid: boolean;
  amount: number;
  status: string;
  userId: string;
  orderId: string;
  order: SaleOrder;
  userName: string;
  store: SaleStore;
  cashierId: string;
  refunded: boolean;
  fiscalSign: string;
  saleNumber: number;
  description: string;
  salePublicId: string;
  refunds: SaleRefund[];
  appletVersion: string;
  externalApiId: string;
  zreportNumber: string;
  receiptNumber: string;
  discountAmount: number;
  payments: SalePayment[];
  servicePaymentAmount: number;
  productList: SaleProductList[];
}

export interface SalePos {
  id: string;
  active: boolean;
  store: SaleStore;
  blocked: boolean;
  updatedBy: string;
  posNumber: number;
  createdBy: string;
  externalId: string;
  erpEnabled: boolean;
  fiscalCardId: string;
  fullPrepayment: boolean;
  posHardwareModel: string;
  externalSystemCode: string;
  dateEdited: SaleDateEdited;
  dateCreated: SaleDateCreated;
  posHardwareSerialNumber: string;
  fastPaymentMethodEnabled: boolean;
  certificatePaymentEnabled: boolean;
}

export interface SaleStore {
  id: string;
  name: string;
  email: string;
  address: string;
  manager: string;
  deleted: boolean;
  industryId: string;
  externalId: string;
  phoneNumber: string;
  description: string;
  interfaceType: string;
  externalSystemCode: string;
  managerPhoneNumber: string;
}

export interface SaleOrder {
  id: string;
  phone: string;
  number: number;
  saleId: string;
  status: string;
  address: string;
  comment: string;
  storeId: string;
  store: SaleStore;
  courierId: string;
  updatedBy: string;
  createdBy: string;
  externalId: string;
  dateCreated: string;
  collectorId: string;
  dateUpdated: string;
  customerName: string;
  discount: SaleDiscount;
  payments: SalePayment[];
  orderedProducts: SaleProduct[];
  acceptedProducts: SaleProduct[];
  rejectedProducts: SaleProduct[];
  servicePayment: SaleServicePayment;
}

export interface SaleRefund {
  id: string;
  fmId: string;
  date: string;
  userId: string;
  amount: number;
  orderId: string;
  userName: string;
  store: SaleStore;
  cashierId: string;
  fiscalSign: string;
  appletVersion: string;
  zreportNumber: string;
  receiptNumber: string;
  payments: SalePayment[];
  productList: SaleProductList[];
}

export interface SaleProduct {
  id: string;
  tax: number;
  psid: string;
  price: number;
  amount: number;
  marked: boolean;
  unitCode: number;
  productId: string;
  barcodes: string[];
  customTotal: number;
  packageCode: string;
  productName: string;
  commissionTIN: string;
  markedLabels: string[];
  discountAmount: number;
  commissionPINFL: string;
  productOwnerType: string;
}

export interface SalePayment {
  id: string;
  date: string;
  amount: number;
  method: string;
  paymentType: string;
  paymentAmount: number;
  transactionNumber: number;
  paymentInfo: SalePaymentInfo;
  paymentTransactionValue: number;
}

export interface SaleDiscount {
  value: number;
  isInPercents: boolean;
}

export interface SaleDateEdited {
  time: number;
  year: number;
  date: number;
  hours: number;
  month: number;
  nanos: number;
  seconds: number;
  minutes: number;
}

export interface SalePaymentInfo {
  rrn: string;
  stan: string;
  authCode: string;
  merchantId: string;
  terminalId: string;
  responseCode: string;
  paymentSystemName: string;
  transactionNumber: number;
}

export interface SaleDateCreated {
  date: number;
  time: number;
  year: number;
  hours: number;
  month: number;
  nanos: number;
  seconds: number;
  minutes: number;
}

export interface SaleProductList {
  id: string;
  tax: number;
  psid: string;
  price: number;
  amount: number;
  marked: boolean;
  unitCode: number;
  productId: string;
  barcodes: string[];
  customTotal: number;
  packageCode: string;
  productName: string;
  commissionTIN: string;
  markedLabels: string[];
  discountAmount: number;
  commissionPINFL: string;
  productOwnerType: string;
  product?: {
    id: string;
    vat: number;
    name: string;
  };
}

export interface SaleServicePayment {
  value: number;
  isInPercents: boolean;
}

export const cleanFilterObject: typeOrdersFilters = {
  dateCreated: null,
};

export type IdOrdersState = {
  data: IContent[];
  filters: typeOrdersFilters;
  pagination: typePagination;
  detailItem: IContent | undefined;
};

export const initialState: IdOrdersState = {
  data: [],
  detailItem: undefined,
  filters: cleanFilterObject,
  pagination: {
    pageNumber: 0,
    itemsPerPage: 14,
  },
};

export const DOrdersArchiveReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(dOrdersArchiveClearFiltersAC, (state) => {
      state.filters = cleanFilterObject;
    })
    .addCase(dOrdersArchiveClearDetailItemAC, (state) => {
      state.detailItem = initialState.detailItem;
    })
    .addCase(dOrdersArchiveSetDataAC, (state, action) => {
      state.data = action.payload.dataList;
    })
    .addCase(dOrdersArchiveSetFiltersAC, (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.pageNumber = 0;
    })
    .addCase(dOrdersArchiveSetPageNumberAC, (state, action) => {
      state.pagination.pageNumber = action.payload.pageNumber;
    })
    .addCase(dOrdersArchiveSetDetailItemAC, (state, action) => {
      state.detailItem = state.data.find((item) => item.id === action.payload);
    })
    .addCase(dOrdersArchiveSetPaginationAC, (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload.pagination };
    })
    .addCase(dOrdersArchiveSetItemsForPageAC, (state, action) => {
      state.pagination = {
        ...state.pagination,
        pageNumber: 0,
        itemsPerPage: action.payload.itemsForPage,
      };
    })
    .addCase(logoutAC, (state) => {
      state.data = initialState.data;
      state.filters = initialState.filters;
      state.detailItem = initialState.detailItem;
      state.pagination = initialState.pagination;
    })
    .addCase(resetReducers, (state, action) => {
      if (action.payload !== "orders") {
        state.data = initialState.data;
        state.filters = initialState.filters;
        state.detailItem = initialState.detailItem;
        state.pagination = initialState.pagination;
      }
    });
});
